<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import Modal from './Modal.vue';
import type { ConfirmOptions } from '@/composables/useConfirm';

// Single confirm dialog instance. Used by ConfirmDialogHost; not typically
// mounted by app code directly. Renders title + body, two buttons, and
// resolves via the `confirm` / `cancel` events.
//
// Focus rule (per plan section A): danger variant focuses Cancel (so the
// Enter key dismisses safely); non-danger focuses Confirm (so Enter accepts).

interface Props {
    open: boolean;
    opts: ConfirmOptions;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    confirm: [];
    cancel: [];
}>();

const danger = computed(() => !!props.opts.danger);
const confirmLabel = computed(() => props.opts.confirmLabel ?? (danger.value ? 'Delete' : 'OK'));
const cancelLabel = computed(() => props.opts.cancelLabel ?? 'Cancel');

const confirmBtn = ref<HTMLButtonElement | null>(null);
const cancelBtn = ref<HTMLButtonElement | null>(null);

// Modal's useFocusTrap auto-focuses the first focusable (the close × button).
// We run our own focus override on a fresh microtask AFTER the Modal mounts
// AND its focus-trap install fires, so the right button lands focused per
// variant: Cancel for danger (Enter dismisses safely), Confirm otherwise.
async function applyDefaultFocus() {
    // Wait for the Teleport, the Transition, and the focus-trap install.
    for (let i = 0; i < 4; i++) await nextTick();
    if (!props.open) return;
    const target = danger.value ? cancelBtn.value : confirmBtn.value;
    target?.focus();
}

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) void applyDefaultFocus();
    },
    { immediate: true },
);
</script>

<template>
    <Modal
        :open="props.open"
        layer="top"
        :title="props.opts.title ?? 'Confirm'"
        :dismiss-on-backdrop="true"
        :dismiss-on-esc="true"
        @close="emit('cancel')"
    >
        <slot name="body">
            <p v-if="props.opts.bodyHtml" class="body" v-html="props.opts.bodyHtml" />
            <p v-else class="body">{{ props.opts.body }}</p>
        </slot>
        <template #footer>
            <button
                ref="cancelBtn"
                type="button"
                class="btn cancel"
                @click="emit('cancel')"
            >
                {{ cancelLabel }}
            </button>
            <button
                ref="confirmBtn"
                type="button"
                class="btn confirm"
                :class="{ danger: danger }"
                @click="emit('confirm')"
            >
                {{ confirmLabel }}
            </button>
        </template>
    </Modal>
</template>

<style scoped>
.body {
    margin: 0;
    line-height: 1.55;
    white-space: pre-wrap;
}

.btn {
    appearance: none;
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: var(--space-xs) var(--space-md);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.btn:hover {
    background: var(--color-bg-hover);
}

.btn:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
}

.btn.confirm {
    background: var(--color-accent);
    border-color: var(--color-accent);
    color: var(--color-text-on-accent, #0a0a0f);
}

.btn.confirm.danger {
    background: var(--color-error);
    border-color: var(--color-error);
    color: var(--color-text-on-accent, #0a0a0f);
}
</style>
